import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase.js';
import { createApi } from '../services/api.js';

interface Settings {
  sensitivity_global: number;
  quarantine_mode: 'off' | 'flag' | 'hide';
  reply_mode: 'off' | 'draft' | 'auto_send';
  reply_tone: 'professional' | 'blocked' | 'custom';
  reply_custom_text: string | null;
  monitored_domains: string[];
  auto_analyze: boolean;
  background_scan: boolean;
  tier?: string;
}

interface Usage {
  tier: string;
  credits: number;
  daily: { used: number; limit: number | null };
  weekly: { used: number; limit: number | null };
  monthly: { used: number; limit: number | null };
  team_id: string | null;
}

const DEFAULT_SETTINGS: Settings = {
  sensitivity_global: 50,
  quarantine_mode: 'off',
  reply_mode: 'off',
  reply_tone: 'professional',
  reply_custom_text: null,
  monitored_domains: [],
  auto_analyze: false,
  background_scan: false,
};

function Toggle({ checked, onChange, label, desc }: { checked: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-surface-200 bg-white p-4">
      <div>
        <p className="text-sm font-medium text-ink">{label}</p>
        <p className="text-xs text-ink-muted">{desc}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-brand' : 'bg-surface-300'}`}
      >
        <span className={`inline-block h-4 w-4 rounded-full bg-white transition transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function Select({ value, onChange, label, desc, options, disabled }: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  desc: string;
  options: { value: string; label: string; tierRequired?: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="rounded-lg border border-surface-200 bg-white p-4">
      <p className="text-sm font-medium text-ink">{label}</p>
      <p className="text-xs text-ink-muted mb-3">{desc}</p>
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const locked = opt.tierRequired && disabled;
          return (
            <label
              key={opt.value}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition ${
                value === opt.value
                  ? 'border-brand bg-red-50/50'
                  : locked
                    ? 'border-surface-100 bg-surface-50 opacity-50 cursor-not-allowed'
                    : 'border-surface-200 hover:border-surface-300'
              }`}
            >
              <input
                type="radio"
                name={label}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => !locked && onChange(opt.value)}
                disabled={!!locked}
                className="accent-brand"
              />
              <span className="text-sm text-ink">{opt.label}</span>
              {opt.tierRequired && (
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                  {opt.tierRequired}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function UsageBar({ used, limit, label }: { used: number; limit: number | null; label: string }) {
  if (limit === null) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex justify-between text-[11px] text-ink-muted mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{used}/{limit}</span>
      </div>
      <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function tierLabel(tier: string) {
  switch (tier) {
    case 'fortress': return 'Fortress';
    case 'citadel': return 'Citadel';
    default: return 'Free';
  }
}

export function SettingsPage({ token }: { token: string }) {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const navigate = useNavigate();

  const api = createApi(token);
  const tier = usage?.tier ?? settings.tier ?? 'free';
  const isFree = tier === 'free';
  const isFortressOrAbove = tier === 'fortress' || tier === 'citadel';
  const isCitadel = tier === 'citadel';

  useEffect(() => {
    api.getSettings().then((result) => {
      if (result.ok && result.data) {
        setSettings({
          ...DEFAULT_SETTINGS,
          ...result.data,
          monitored_domains: result.data.monitored_domains ?? [],
        });
      }
    });
    api.getUsage().then((result) => {
      if (result.ok) setUsage(result.data);
    });
  }, [token]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    const result = await api.updateSettings({
      sensitivity_global: settings.sensitivity_global,
      quarantine_mode: settings.quarantine_mode,
      reply_mode: settings.reply_mode,
      reply_tone: settings.reply_tone,
      reply_custom_text: settings.reply_custom_text,
      monitored_domains: settings.monitored_domains,
      auto_analyze: settings.auto_analyze,
      background_scan: settings.background_scan,
    });
    setSaving(false);
    if (result.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  }

  function addDomain() {
    const d = newDomain.trim().toLowerCase().replace(/^@/, '');
    if (d && !settings.monitored_domains.includes(d)) {
      setSettings({ ...settings, monitored_domains: [...settings.monitored_domains, d] });
      setNewDomain('');
    }
  }

  function removeDomain(domain: string) {
    setSettings({ ...settings, monitored_domains: settings.monitored_domains.filter(d => d !== domain) });
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError('');
    const result = await api.deleteAccount();
    setDeleting(false);
    if (result.ok) {
      await supabase.auth.signOut();
      navigate('/login');
    } else {
      setDeleteError(result.error.message);
    }
  }

  async function handleExportData() {
    const result = await api.exportData();
    if (result.ok) {
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aas-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-ink mb-6">Settings</h1>

      <div className="max-w-lg space-y-6">
        {/* Usage section */}
        {usage && (
          <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-surface-100 flex items-center justify-between">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Usage</p>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                tier === 'free' ? 'bg-surface-100 text-ink-muted' :
                tier === 'fortress' ? 'bg-blue-50 text-blue-700' :
                'bg-purple-50 text-purple-700'
              }`}>{tierLabel(tier)}</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              <UsageBar used={usage.daily.used} limit={usage.daily.limit} label="Today" />
              <UsageBar used={usage.weekly.used} limit={usage.weekly.limit} label="This week" />
              <UsageBar used={usage.monthly.used} limit={usage.monthly.limit} label="This month" />
              {tier !== 'free' && (
                <p className="text-xs text-emerald-600 font-medium">Unlimited analyses</p>
              )}
              {usage.credits > 0 && (
                <p className="text-xs text-blue-600">+{usage.credits} bonus credits available</p>
              )}
            </div>
          </div>
        )}

        {/* Sensitivity */}
        <div className="rounded-lg border border-surface-200 bg-white p-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-ink">Sensitivity</label>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              settings.sensitivity_global <= 30 ? 'bg-emerald-50 text-emerald-600' :
              settings.sensitivity_global <= 60 ? 'bg-amber-50 text-amber-600' :
              settings.sensitivity_global <= 80 ? 'bg-orange-50 text-orange-600' :
              'bg-red-50 text-red-600'
            }`}>
              {settings.sensitivity_global <= 30 ? 'Lenient' :
               settings.sensitivity_global <= 60 ? 'Moderate' :
               settings.sensitivity_global <= 80 ? 'Strict' : 'Maximum'}
            </span>
          </div>
          <p className="mb-4 text-xs text-ink-muted">
            Higher values flag more emails. Adjust based on how aggressive your inbox is.
          </p>

          {/* Progress bar visualization */}
          <div className="relative mb-2">
            <div className="h-3 w-full rounded-full bg-surface-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-200 ${
                  settings.sensitivity_global <= 30 ? 'bg-emerald-500' :
                  settings.sensitivity_global <= 60 ? 'bg-amber-500' :
                  settings.sensitivity_global <= 80 ? 'bg-orange-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${settings.sensitivity_global}%` }}
              />
            </div>
            {/* Segment markers */}
            <div className="absolute inset-0 flex">
              {[30, 60, 80].map((mark) => (
                <div
                  key={mark}
                  className="absolute top-0 h-3 w-px bg-white/40"
                  style={{ left: `${mark}%` }}
                />
              ))}
            </div>
          </div>

          <input
            type="range"
            min={0}
            max={100}
            value={settings.sensitivity_global}
            onChange={(e) => setSettings({ ...settings, sensitivity_global: Number(e.target.value) })}
            className="w-full accent-brand h-0 opacity-0 cursor-pointer"
            style={{ marginTop: '-14px', height: '24px', opacity: 0, position: 'relative', zIndex: 1 }}
          />

          <div className="flex justify-between text-[10px] text-ink-light mt-1">
            <span>0</span>
            <span>30</span>
            <span>60</span>
            <span>80</span>
            <span>100</span>
          </div>
          <div className="flex justify-between text-[10px] text-ink-light mt-0.5">
            <span>Lenient</span>
            <span className="text-center">Moderate</span>
            <span className="text-right">Strict</span>
          </div>
        </div>

        {/* Domain filtering */}
        <div className="rounded-lg border border-surface-200 bg-white p-4">
          <label className="text-sm font-medium text-ink">Monitored domains</label>
          <p className="mb-3 text-xs text-ink-muted">
            Only auto-analyze emails from these domains. Leave empty to analyze all.
          </p>

          {settings.monitored_domains.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {settings.monitored_domains.map((d) => (
                <span key={d} className="inline-flex items-center gap-1 rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-xs text-ink">
                  @{d}
                  <button
                    onClick={() => removeDomain(d)}
                    className="ml-1 text-ink-light hover:text-red-500 transition"
                    aria-label={`Remove ${d}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
              placeholder="company.com"
              className="flex-1 rounded-lg border border-surface-200 px-3 py-2 text-sm text-ink placeholder:text-surface-300 focus:border-brand focus:outline-none"
            />
            <button
              onClick={addDomain}
              disabled={!newDomain.trim()}
              className="rounded-lg bg-surface-100 px-4 py-2 text-sm font-medium text-ink hover:bg-surface-200 disabled:opacity-50 transition"
            >
              Add
            </button>
          </div>
        </div>

        {/* Auto-analyze toggle */}
        <Toggle
          label="Auto-analyze on open"
          desc="Automatically analyze emails when you open them. If off, use the manual button."
          checked={settings.auto_analyze}
          onChange={(v) => setSettings({ ...settings, auto_analyze: v })}
        />

        {/* Background scanning toggle */}
        <Toggle
          label="Background scanning"
          desc="Scan new emails every 5 minutes, even when Gmail is closed. Applies your quarantine and reply settings automatically. Open Gmail once to activate. Uses your analysis quota."
          checked={settings.background_scan}
          onChange={(v) => setSettings({ ...settings, background_scan: v })}
        />

        {/* Quarantine mode */}
        <Select
          label="Quarantine"
          desc="What happens when a toxic email (score 61+) is detected."
          value={settings.quarantine_mode}
          onChange={(v) => setSettings({ ...settings, quarantine_mode: v as Settings['quarantine_mode'] })}
          disabled={isFree}
          options={[
            { value: 'off', label: 'Off — no quarantine action' },
            { value: 'flag', label: 'Flag — label as AAS/Quarantine, keep in inbox', tierRequired: isFree ? 'Fortress' : undefined },
            { value: 'hide', label: 'Hide — label + archive (remove from inbox)', tierRequired: !isCitadel ? 'Citadel' : undefined },
          ]}
        />

        {/* Reply mode */}
        <Select
          label="Auto-reply"
          desc="Automatically respond to toxic emails."
          value={settings.reply_mode}
          onChange={(v) => setSettings({ ...settings, reply_mode: v as Settings['reply_mode'] })}
          disabled={isFree}
          options={[
            { value: 'off', label: 'Off — no automatic reply' },
            { value: 'draft', label: 'Draft — create reply draft for your review', tierRequired: isFree ? 'Fortress' : undefined },
            { value: 'auto_send', label: 'Auto-send — reply immediately without review', tierRequired: !isCitadel ? 'Citadel' : undefined },
          ]}
        />

        {/* Reply tone (only shown when reply_mode is not 'off') */}
        {settings.reply_mode !== 'off' && (
          <>
            <Select
              label="Reply tone"
              desc="The tone of the automatic reply."
              value={settings.reply_tone}
              onChange={(v) => setSettings({ ...settings, reply_tone: v as Settings['reply_tone'] })}
              options={[
                { value: 'professional', label: 'Professional — AI-generated diplomatic response' },
                { value: 'blocked', label: 'Blocked — inform sender the message was not delivered' },
                { value: 'custom', label: 'Custom — your own message template' },
              ]}
            />

            {/* Custom text (only shown when reply_tone is 'custom') */}
            {settings.reply_tone === 'custom' && (
              <div className="rounded-lg border border-surface-200 bg-white p-4">
                <label className="text-sm font-medium text-ink">Custom reply template</label>
                <p className="mb-3 text-xs text-ink-muted">
                  This message will be sent as-is to the sender of toxic emails.
                </p>
                <textarea
                  value={settings.reply_custom_text ?? ''}
                  onChange={(e) => setSettings({ ...settings, reply_custom_text: e.target.value || null })}
                  placeholder="Thank you for your message. We expect all communications to maintain a professional and respectful tone..."
                  rows={4}
                  maxLength={500}
                  className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-ink placeholder:text-surface-300 focus:border-brand focus:outline-none resize-none"
                />
                <p className="mt-1 text-right text-[10px] text-ink-light">
                  {(settings.reply_custom_text ?? '').length}/500
                </p>
              </div>
            )}
          </>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full rounded-lg bg-ink py-2.5 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50 transition"
        >
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
        </button>

        {/* Danger zone */}
        <div className="mt-12 rounded-xl border border-red-200 bg-red-50/50 overflow-hidden">
          <div className="px-5 py-3 border-b border-red-200">
            <p className="text-[11px] font-medium text-red-600 uppercase tracking-wider">Danger zone</p>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-ink">Export my data</p>
                <p className="text-xs text-ink-muted">Download all your data as JSON (GDPR)</p>
              </div>
              <button
                onClick={handleExportData}
                className="shrink-0 rounded-lg border border-surface-200 bg-white px-4 py-2 text-sm font-medium text-ink hover:bg-surface-50 transition"
              >
                Export
              </button>
            </div>
            <div className="border-t border-red-200" />
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-red-700">Delete my account</p>
                <p className="text-xs text-ink-muted">Permanently delete your account and all data</p>
              </div>
              <button
                onClick={() => { setShowDeleteModal(true); setDeleteConfirm(''); setDeleteError(''); }}
                className="shrink-0 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition"
              >
                Delete account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold text-ink mb-2">Delete account</h2>
            <p className="text-sm text-ink-muted mb-4">
              This action is permanent. All your data, analyses, and settings will be deleted and cannot be recovered.
            </p>
            <label className="block text-sm font-medium text-ink mb-2">
              Type <span className="font-mono text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-ink placeholder:text-surface-300 focus:border-red-500 focus:outline-none mb-4"
              autoFocus
            />
            {deleteError && (
              <p className="text-xs text-red-600 mb-3">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 rounded-lg border border-surface-200 bg-white py-2 text-sm font-medium text-ink hover:bg-surface-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== 'DELETE' || deleting}
                className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition"
              >
                {deleting ? 'Deleting...' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
